import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { trackPageView } from './utils/analytics';
import LoginPage from './pages/LoginPage';
import OrganizationRegisterPage from './pages/OrganizationRegisterPage';
import LandingPage from './pages/LandingPage';
import SecurityPage from './pages/SecurityPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import PricingPage from './pages/PricingPage';
import Dashboard from './pages/Dashboard';
import LibraryPage from './pages/LibraryPage';
import KnowledgeDetailPage from './pages/KnowledgeDetailPage';
import CreateKnowledgePage from './pages/CreateKnowledgePage';
import UserManagementPage from './pages/UserManagementPage';
import NotionConfigPage from './pages/NotionConfigPage';
import ConfluenceConfigPage from './pages/ConfluenceConfigPage';
import IntegrationsPage from './pages/IntegrationsPage';
import BillingPage from './pages/BillingPage';
import MorePage from './pages/MorePage';
import Layout from './components/Layout';
import { Analytics } from "@vercel/analytics/react"

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user is active
  if (user.is_active === false) {
    localStorage.clear();
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Admin Only Route wrapper
function AdminRoute({ children }) {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  // Must be admin role
  if (user.role !== 'admin') {
    // Non-admins get redirected to dashboard with error message
    alert('Access denied. Admin privileges required.');
    return <Navigate to="/dashboard" replace />;
  }
  
  // Check if user is active
  if (user.is_active === false) {
    localStorage.clear();
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Manager or Admin Route wrapper (for future features)
// eslint-disable-next-line no-unused-vars
function ManagerRoute({ children }) {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Must be admin or manager
  if (!['admin', 'manager'].includes(user.role)) {
    alert('Access denied. Manager or Admin privileges required.');
    return <Navigate to="/dashboard" replace />;
  }

  // Check if user is active
  if (user.is_active === false) {
    localStorage.clear();
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Page tracking component
function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

function App() {
  return (
    <Router>
      <PageTracker />
      <Routes>
        {/* Public routes - no layout */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<OrganizationRegisterPage />} />
        
        {/* Protected routes - with layout */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/library"
          element={
            <ProtectedRoute>
              <Layout>
                <LibraryPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge/:id"
          element={
            <ProtectedRoute>
              <Layout>
                <KnowledgeDetailPage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/create"
          element={
            <ProtectedRoute>
              <Layout>
                <CreateKnowledgePage />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/more"
          element={
            <ProtectedRoute>
              <Layout>
                <MorePage />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Admin only routes */}
        <Route
          path="/users"
          element={
            <AdminRoute>
              <Layout>
                <UserManagementPage />
              </Layout>
            </AdminRoute>
          }
        />
        <Route
          path="/integrations"
          element={
            <AdminRoute>
              <Layout>
                <IntegrationsPage />
              </Layout>
            </AdminRoute>
          }
        />
        <Route
          path="/notion"
          element={
            <AdminRoute>
              <Layout>
                <NotionConfigPage />
              </Layout>
            </AdminRoute>
          }
        />
        <Route
          path="/integrations/confluence"
          element={
            <AdminRoute>
              <Layout>
                <ConfluenceConfigPage />
              </Layout>
            </AdminRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <AdminRoute>
              <Layout>
                <BillingPage />
              </Layout>
            </AdminRoute>
          }
        />

        {/* 404 - Catch all redirects to landing page for logged out users, dashboard for logged in */}
        <Route path="*" element={
          localStorage.getItem('token') 
            ? <Navigate to="/dashboard" replace /> 
            : <Navigate to="/" replace />
        } />
      </Routes>
      <Analytics />
    </Router>
  );
}

export default App;